import { useState, useCallback, useMemo, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Users, Plus } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '@/components/ui/use-toast';
import { Role } from '@/types/models';
import { UserPermissions, permissionService, GroupedPermissionsResponse } from '@/services/permission.service';
import { roleService } from '@/services/role.service';
import { usePerformanceTracking } from '@/services/performance.service';
import { getEmployees, Employee, assignRoleToUser as apiAssignRoleToUser, unassignRoleFromUser as apiUnassignRoleFromUser } from '@/services/employee.service';
import { RolesTable, RoleStats } from './components/RolesTable';
import { CreateRoleForm } from './components/CreateRoleForm';
import { AssignUserDialog } from './components/AssignUserDialog';
import { EditRoleDialog } from './components/EditRoleDialog';
import { DeleteRoleDialog } from './components/DeleteRoleDialog';
import { ViewUsersDialog } from './components/ViewUsersDialog';

// 🔥 BOOLEAN UTILITY: Helper function to ensure strict boolean values throughout the component

// 🔥 BOOLEAN VALIDATION: Helper to validate permission objects
const validatePermissionStructure = (permissions: any): boolean => {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }

  try {
    Object.values(permissions).forEach((module: any) => {
      if (module && typeof module === 'object') {
        Object.values(module).forEach((submodule: any) => {
          if (submodule && typeof submodule === 'object') {
            Object.values(submodule).forEach((_permission: any) => {
              // Validate permission values are boolean
            });
          }
        });
      }
    });
    return true;
  } catch (error) {
    return false;
  }
};

interface RoleTemplate {
  id: string;
  name: string;
  description: string;
  permissions: UserPermissions;
  category: string;
}

// Define permission groups based on the backend API structure
const permissionGroups = {
  employee_management: {
    description: 'Employee Management',
    modules: {
      employees: 'Employee Management',
      department: 'Department Management',
      agreement: 'Agreement Management',
      compensation: 'Compensation Management'
    }
  },
  sales_management: {
    description: 'Sales & Clients',
    modules: {
      client: 'Client Management',
      proposal: 'Proposal Management',
      contract: 'Contract Management',
      revenue: 'Revenue Management'
    }
  },
  finance_management: {
    description: 'Finance',
    modules: {
      invoice: 'Invoice Management',
      expense: 'Expense Management',
      financialReport: 'Financial Reports'
    }
  },
  hiring: {
    description: 'Hiring Management',
    modules: {
      candidate: 'Candidate Management',
      jobPosting: 'Job Management',
      onboarding: 'Onboarding Management',
      settings: {
        onboardingTask: 'Onboarding Task Management'
      }
    }
  },
  project_management: {
    description: 'Project Management',
    modules: {
      project: 'Project Management',
      task: 'Task Management',
      projectStatus: 'Status Settings'
    }
  },
  settings: {
    description: 'Settings',
    modules: {
      roleAndPermission: 'Roles and Permissions Management',
      jobTitle: 'Job Title Management'
    }
  },
  system: {
    description: 'System',
    modules: {
      system: 'System Administration'
    }
  }
};

// Role templates with the new permission structure
const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    id: 'super_admin',
    name: 'Super Admin',
    description: 'Full system access with all permissions',
    permissions: {
      employee_management: {
        employees: { view: true, create: true, edit: true, delete: true },
        department: { view: true, create: true, edit: true, delete: true },
        agreement: { view: true, edit: true },
        compensation: { view: true, edit: true }
      },
      sales_management: {
        client: { view: true, create: true, edit: true, delete: true },
        proposal: { view: true, create: true, edit: true, delete: true },
        contract: { view: true, create: true, edit: true, delete: true },
        revenue: { view: true }
      },
      finance_management: {
        invoice: { view: true, create: true, edit: true, delete: true },
        expense: { view: true, create: true, edit: true, delete: true },
        financialReport: { view: true }
      },
      hiring: {
        candidate: { view: true, create: true, edit: true, delete: true },
        jobPosting: { view: true, create: true, edit: true, delete: true },
        onboarding: { view: true, create: true, edit: true, delete: true },
        settings: {
          onboardingTask: { view: true, create: true, edit: true, delete: true }
        }
      },
      project_management: {
        project: { view: true, create: true, edit: true, delete: true },
        task: { view: true, create: true, edit: true, delete: true },
        projectStatus: { view: true, create: true, edit: true, delete: true }
      },
      settings: {
        roleAndPermission: { view: true, create: true, edit: true, delete: true },
        jobTitle: { view: true, create: true, edit: true, delete: true }
      } as any,
      system: {
        system: { admin: true }
      }
    },
    category: 'System'
  },
  {
    id: 'hr_manager',
    name: 'HR Manager',
    description: 'Complete HR management and oversight',
    permissions: {
      employee_management: {
        employees: { view: true, create: true, edit: true, delete: false },
        department: { view: true, create: true, edit: true, delete: false },
        agreement: { view: true, edit: true },
        compensation: { view: true, edit: true }
      },
      sales_management: {
        client: { view: true, create: true, edit: true, delete: false },
        proposal: { view: true, create: true, edit: true, delete: false },
        contract: { view: true, create: true, edit: true, delete: false },
        revenue: { view: true }
      },
      finance_management: {
        invoice: { view: true, create: true, edit: true, delete: false },
        expense: { view: true, create: true, edit: true, delete: false },
        financialReport: { view: true }
      },
      hiring: {
        candidate: { view: true, create: true, edit: true, delete: false },
        jobPosting: { view: true, create: true, edit: true, delete: false },
        onboarding: { view: true, create: true, edit: true, delete: false },
        settings: {
          onboardingTask: { view: true, create: true, edit: true, delete: false }
        }
      },
      project_management: {
        project: { view: true, create: true, edit: true, delete: false },
        task: { view: true, create: true, edit: true, delete: false },
        projectStatus: { view: true, create: true, edit: true, delete: false }
      },
      settings: {
        roleAndPermission: { view: true, create: true, edit: true, delete: false },
        jobTitle: { view: true, create: true, edit: true, delete: false }
      } as any,
      system: {
        system: { admin: false }
      }
    },
    category: 'HR'
  },
  {
    id: 'employee',
    name: 'Employee',
    description: 'Basic employee access',
    permissions: {
      employee_management: {
        employees: { view: false, create: false, edit: false, delete: false },
        department: { view: false, create: false, edit: false, delete: false },
        agreement: { view: false, edit: false },
        compensation: { view: false, edit: false }
      },
      sales_management: {
        client: { view: false, create: false, edit: false, delete: false },
        proposal: { view: false, create: false, edit: false, delete: false },
        contract: { view: false, create: false, edit: false, delete: false },
        revenue: { view: false }
      },
      finance_management: {
        invoice: { view: false, create: false, edit: false, delete: false },
        expense: { view: false, create: false, edit: false, delete: false },
        financialReport: { view: false }
      },
      hiring: {
        candidate: { view: false, create: false, edit: false, delete: false },
        jobPosting: { view: true, create: false, edit: false, delete: false },
        onboarding: { view: false, create: false, edit: false, delete: false },
        settings: {

          onboardingTask: { view: false, create: false, edit: false, delete: false }
        }
      },
      project_management: {
        project: { view: false, create: false, edit: false, delete: false },
        task: { view: false, create: false, edit: false, delete: false },
        projectStatus: { view: false, create: false, edit: false, delete: false }
      },
      settings: {
        roleAndPermission: { view: false, create: false, edit: false, delete: false },
        jobTitle: { view: false, create: false, edit: false, delete: false }
      } as any,
      system: {
        system: { admin: false }
      }
    },
    category: 'Basic'
  }
];

const ALL_POSSIBLE_PERMISSIONS: UserPermissions = {
  employee_management: {
    employees: { view: false, create: false, edit: false, delete: false },
    department: { view: false, create: false, edit: false, delete: false },
    agreement: { view: false, edit: false },
    compensation: { view: false, edit: false }
  },
  sales_management: {
    client: { view: false, create: false, edit: false, delete: false },
    proposal: { view: false, create: false, edit: false, delete: false },
    contract: { view: false, create: false, edit: false, delete: false },
    revenue: { view: false }
  },
  finance_management: {
    invoice: { view: false, create: false, edit: false, delete: false },
    expense: { view: false, create: false, edit: false, delete: false },
    financialReport: { view: false }
  },
  hiring: {
    candidate: { view: false, create: false, edit: false, delete: false },
    jobPosting: { view: false, create: false, edit: false, delete: false },
    onboarding: { view: false, create: false, edit: false, delete: false },
    settings: {
      onboardingTask: { view: false, create: false, edit: false, delete: false }
    }
  },
  project_management: {
    project: { view: false, create: false, edit: false, delete: false },
    task: { view: false, create: false, edit: false, delete: false },
    projectStatus: { view: false, create: false, edit: false, delete: false }
  },
  settings: {
    roleAndPermission: { view: false, create: false, edit: false, delete: false },
    jobTitle: { view: false, create: false, edit: false, delete: false }
  } as any,
  system: {
    system: { admin: false }
  }
};


// Custom hooks for better state management
const useRoleManagement = () => {
  const {
    roles,
    userRoles,
    updateRole,
    createRole,
    deleteRole,
    assignRoleToUser,
    unassignRoleFromUser,
    hasPermission,
    users,
    user,
    refreshRoles,
    refreshUsers,

  } = useAuthStore();

  const [newRoleName, setNewRoleName] = useState<string>('');
  const [newRolePermissions, setNewRolePermissions] = useState<UserPermissions>(
    JSON.parse(JSON.stringify(ALL_POSSIBLE_PERMISSIONS))
  );
  const [newRoleDescription, setNewRoleDescription] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isAssignUserDialogOpen, setIsAssignUserDialogOpen] = useState<boolean>(false);
  const [roleToAssignUser, setRoleToAssignUser] = useState<Role | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  const [roleSearchTerm, setRoleSearchTerm] = useState<string>('');
  const [viewUsersRole, setViewUsersRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<RoleTemplate | null>(null);

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRolePermissions, setEditingRolePermissions] = useState<UserPermissions | null>(null);
  const [editingRoleName, setEditingRoleName] = useState<string>('');
  const [editingRoleDescription, setEditingRoleDescription] = useState<string>('');
  // 🔥 NEW: State for permissions API data
  const [allPermissions, setAllPermissions] = useState<GroupedPermissionsResponse | null>(null);
  const [rolePermissionsCache, setRolePermissionsCache] = useState<{ [roleId: string]: UserPermissions }>({});
  const [isLoadingPermissions, setIsLoadingPermissions] = useState<boolean>(false);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);
  // 🔥 NEW: State for API flags to control which permission groups are shown
  const [salesClientsFlag, setSalesClientsFlag] = useState<boolean | undefined>(undefined);
  const [projectManagementFlag, setProjectManagementFlag] = useState<boolean | undefined>(undefined);
  const [financeManagementFlag, setFinanceManagementFlag] = useState<boolean | undefined>(undefined);

  // 🔥 NEW: State for tracking changes before saving to backend

  // 🔥 NEW: Fetch API flags for permission group filtering
  const fetchPermissionFlags = useCallback(async () => {
    try {
      const permissionsData = await permissionService.getUserPermissions();
      setSalesClientsFlag(permissionsData.sales_clients);
      setProjectManagementFlag(permissionsData.project_management);
      setFinanceManagementFlag(permissionsData.finance_management);
    } catch (error: any) {
      console.warn('Failed to fetch permission flags from API:', error);
      // Don't throw error - allow component to work without flags
      // Default behavior: show all groups if flags are not available
    }
  }, []);

  // 🔥 NEW: Fetch all permissions from API (optional - only if user has permission)
  const fetchAllPermissions = useCallback(async () => {
    try {
      setIsLoadingPermissions(true);
      setPermissionsError(null);

      const permissions = await permissionService.getAllPermissions();
      setAllPermissions(permissions);

      return permissions;
    } catch (error: any) {
      console.warn('Failed to fetch permissions from API:', error);
      setPermissionsError(error.message || 'Failed to fetch permissions');

      // Don't throw error - allow component to work without permissions API
      // This is needed for users who have role permissions but not permission.view
      return null;
    } finally {
      setIsLoadingPermissions(false);
    }
  }, []);

  // 🔥 NEW: Fetch permissions for a specific role
  const fetchRolePermissions = useCallback(async (roleId: string) => {
    try {
      setIsLoadingPermissions(true);
      setPermissionsError(null);

      const permissions = await permissionService.getRolePermissions(roleId);

      if (permissions) {
        // Cache the permissions
        setRolePermissionsCache(prev => ({
          ...prev,
          [roleId]: permissions
        }));

        return permissions;
      } else {
        // Try to get stored permissions from localStorage
        const storedPermissions = roleService.getStoredPermissionsById(roleId);
        if (storedPermissions) {
          setRolePermissionsCache(prev => ({
            ...prev,
            [roleId]: storedPermissions
          }));
          return storedPermissions;
        }

        console.warn('⚠️ No permissions available for role:', roleId);
        return null;
      }
    } catch (error: any) {
      console.warn('⚠️ Error fetching role permissions:', error);

      // Try to get stored permissions as fallback
      const storedPermissions = roleService.getStoredPermissionsById(roleId);
      if (storedPermissions) {
        setRolePermissionsCache(prev => ({
          ...prev,
          [roleId]: storedPermissions
        }));
        return storedPermissions;
      }

      setPermissionsError('No permissions available for this role');
      return null;
    } finally {
      setIsLoadingPermissions(false);
    }
  }, []);

  // 🔥 NEW: Create role with permissions and sync to backend
  const createRoleWithPermissions = useCallback(async (roleName: string, permissions: UserPermissions, description?: string) => {
    try {
      setIsLoading(true);

      // Create role data
      const roleData: Partial<Role> = {
        name: roleName,
        description: description || '',
        permissions: permissions
      };

      // Use AuthContext createRole which handles backend API call and transformation
      const result = await createRole(roleData);

      return result;
    } catch (error: any) {
      console.error('❌ Error in createRoleWithPermissions:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [createRole]);

  // 🔥 NEW: Transform permissions from object to array format for backend
  const transformPermissionsToArray = useCallback((permissions: UserPermissions): string[] => {
    const permissionsArray: string[] = [];

    // Check if permissions object is valid
    if (!permissions || typeof permissions !== 'object') {
      return permissionsArray;
    }

    // 🔥 FIX: Transform each module and include ALL permissions (true ones only for backend)
    // But we need to ensure we're sending the complete state, not just true permissions
    Object.entries(permissions).forEach(([module, modulePerms]) => {
      if (modulePerms && typeof modulePerms === 'object') {
        Object.entries(modulePerms).forEach(([submodule, submodulePerms]) => {
          if (submodulePerms && typeof submodulePerms === 'object') {
            // Handle nested structure (like hiring.settings.status)
            if (submodule === 'settings' && typeof submodulePerms === 'object') {
              Object.entries(submodulePerms).forEach(([nestedSubmodule, nestedPerms]) => {
                if (nestedPerms && typeof nestedPerms === 'object') {
                  Object.entries(nestedPerms).forEach(([action, hasPermission]) => {
                    // 🔥 CRITICAL FIX: Only add permissions that are explicitly true
                    // Backend expects only enabled permissions in the array
                    if (hasPermission === true) {
                      // 🔥 FIX: Backend expects format like "hiring.status.view" not "hiring.settings.status.view"
                      // This flattens the nested structure to match backend expectations
                      const permissionString = `${module}.${nestedSubmodule}.${action}`;
                      permissionsArray.push(permissionString);
                    }
                  });
                }
              });
            } else {
              // Handle regular structure (like hiring.candidate)
              Object.entries(submodulePerms).forEach(([action, hasPermission]) => {
                // 🔥 CRITICAL FIX: Only add permissions that are explicitly true
                // Backend expects only enabled permissions in the array
                if (hasPermission === true) {
                  // 🔥 FIX: For onboarding, backend expects format like "onboarding.create" not "hiring.onboarding.create"
                  let permissionString;
                  if (submodule === 'onboarding') {
                    permissionString = `${submodule}.${action}`;
                  } else if (submodule === 'agreement') {
                    permissionString = `${submodule}.${action}`;
                  } else if (submodule === 'compensation') {
                    permissionString = `${submodule}.${action}`;
                  } else if (submodule === 'client' || submodule === 'proposal' || submodule === 'contract' || submodule === 'revenue') {
                    permissionString = `${submodule}.${action}`;
                  } else if (submodule === 'invoice' || submodule === 'expense' || submodule === 'financialReport') {
                    permissionString = `${submodule}.${action}`;
                  } else if (module === 'settings' && submodule === 'jobTitle') {
                    permissionString = `${submodule}.${action}`;
                  } else if (module === 'settings' && submodule === 'roleAndPermission') {
                    permissionsArray.push(`user.${action}`);
                    permissionsArray.push(`role.${action}`);
                    if (action === 'view') {
                      permissionsArray.push(`permission.view`);
                    } else if (action === 'create' || action === 'edit' || action === 'delete') {
                      if (!permissionsArray.includes(`permission.manage`)) {
                        permissionsArray.push(`permission.manage`);
                      }
                    }
                    return; // Skip the final push since we pushed multiple
                  } else if (module === 'project_management' && (submodule === 'project' || submodule === 'task' || submodule === 'projectStatus')) {
                    // Project Management permissions should be in simple format "project.action", "task.action", or "projectStatus.action"
                    permissionString = `${submodule}.${action}`;
                  } else {
                    permissionString = `${module}.${submodule}.${action}`;
                  }
                  permissionsArray.push(permissionString);
                }
                // Note: False permissions are handled by their absence from the array
                // Backend will understand that missing permissions = disabled
              });
            }
          }
        });
      }
    });

    // 🔥 IMPORTANT: Empty array is valid - it means no permissions are enabled
    // Don't warn about empty array as it's a valid state when all permissions are disabled

    return permissionsArray;
  }, []);

  // 🔥 NEW: Update role permissions and sync to backend
  const updateRolePermissions = useCallback(async (roleId: string, permissions: UserPermissions) => {
    try {
      setIsLoading(true);

      // 🔥 FIX: Transform permissions to array format for backend
      const permissionsArray = transformPermissionsToArray(permissions);

      // 🔥 IMPORTANT: Empty array is valid - means all permissions are disabled
      // This is actually the fix for the uncheck/disable issue!

      // 🔥 FIX: Get the role details to send complete data
      const rolesArray = Array.isArray(roles) ? roles : [];
      const currentRole = rolesArray.find(r => r.id === roleId || r._id === roleId);

      // 🔥 FIX: Send complete role data in backend expected format
      const roleUpdateData = {
        name: currentRole?.name || 'Unknown Role',
        description: currentRole?.description || '',
        permissions: permissionsArray, // This can be empty array for all disabled permissions
        isActive: true
      };


      // Update permissions via backend API with proper format
      const updatedRole = await roleService.updateRole(roleId, roleUpdateData);

      // 🔥 NEW: Check if backend returned empty permissions
      if (!updatedRole.permissions || updatedRole.permissions.length === 0) {
        // This is actually EXPECTED when all permissions are disabled!
        // Only log error if we sent permissions but got none back
        if (permissionsArray.length > 0) {
          return {
            ...updatedRole,
            _hasEmptyPermissionsError: true
          };
        }
      }

      // Update cache
      setRolePermissionsCache(prev => ({
        ...prev,
        [roleId]: permissions
      }));

      return updatedRole;
    } catch (error: any) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [transformPermissionsToArray, roles]);

  const userCountsByRole = useMemo(() => {
    const counts: { [roleName: string]: number } = {};

    // Defensive: Ensure roles is an array
    const rolesArray = Array.isArray(roles) ? roles : [];
    rolesArray.forEach(role => {
      counts[role.name] = 0;
    });

    // Defensive: Ensure users is an array
    const usersArray = Array.isArray(users) ? users : [];

    // Calculate user counts by role

    usersArray.forEach(user => {
      if (user) {
        let userAssignedToRole = false;

        // 🔥 IMPROVED: Check roles array first (most reliable)
        if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
          user.roles.forEach(userRole => {
            // Handle both string and object role formats
            let roleName = '';
            if (typeof userRole === 'string') {
              roleName = userRole;
            } else if (typeof userRole === 'object' && userRole) {
              roleName = (userRole as any).name || (userRole as any)._id || (userRole as any).id || '';
            }

            // Check if this role name exists in our counts
            if (roleName && counts[roleName] !== undefined) {
              counts[roleName]++;
              userAssignedToRole = true;
            } else if (roleName) {
              // Try to find role by ID matching
              const matchingRole = rolesArray.find(r =>
                r.id === roleName || r._id === roleName || r.name === roleName
              );
              if (matchingRole && counts[matchingRole.name] !== undefined) {
                counts[matchingRole.name]++;
                userAssignedToRole = true;
              }
            }
          });
        }

        // 🔥 FALLBACK: Check traditional role field if not assigned via roles array
        if (!userAssignedToRole && user.role) {
          let roleName = '';
          if (typeof user.role === 'string') {
            roleName = user.role;
          } else if (typeof user.role === 'object' && user.role) {
            roleName = (user.role as any).name || (user.role as any)._id || (user.role as any).id || '';
          }

          if (roleName && counts[roleName] !== undefined) {
            counts[roleName]++;
            userAssignedToRole = true;
          } else if (roleName) {
            // Try to find role by ID matching
            const matchingRole = rolesArray.find(r =>
              r.id === roleName || r._id === roleName || r.name === roleName
            );
            if (matchingRole && counts[matchingRole.name] !== undefined) {
              counts[matchingRole.name]++;
              userAssignedToRole = true;
            }
          }
        }

        // Users without valid role assignments are not counted
      }
    });

    return counts;
  }, [users, roles]);

  return {
    roles,
    userRoles,
    updateRole,
    createRole,
    deleteRole,
    assignRoleToUser,
    unassignRoleFromUser,
    hasPermission,
    users,
    user,
    refreshRoles,
    refreshUsers,
    newRoleName,
    setNewRoleName,
    newRolePermissions,
    setNewRolePermissions,
    newRoleDescription,
    setNewRoleDescription,
    selectedRole,
    setSelectedRole,
    isAssignUserDialogOpen,
    setIsAssignUserDialogOpen,
    roleToAssignUser,
    setRoleToAssignUser,
    userSearchTerm,
    setUserSearchTerm,
    roleSearchTerm,
    setRoleSearchTerm,
    viewUsersRole,
    setViewUsersRole,
    isLoading,
    setIsLoading,
    selectedTemplate,
    setSelectedTemplate,
    selectedRoles,
    setSelectedRoles,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    roleToDelete,
    setRoleToDelete,
    editingRole,
    setEditingRole,
    isEditModalOpen,
    setIsEditModalOpen,
    editingRolePermissions,
    setEditingRolePermissions,
    editingRoleName,
    setEditingRoleName,
    editingRoleDescription,
    setEditingRoleDescription,
    userCountsByRole,
    allPermissions,
    isLoadingPermissions,
    rolePermissionsCache,
    permissionsError,
    fetchAllPermissions,
    fetchRolePermissions,
    createRoleWithPermissions,
    updateRolePermissions,
    transformPermissionsToArray,
    salesClientsFlag,
    setSalesClientsFlag,
    projectManagementFlag,
    setProjectManagementFlag,
    financeManagementFlag,
    setFinanceManagementFlag,
    fetchPermissionFlags
  };
};

// Simple debounce utility function with optimized timing
const debounce = <T extends (...args: any[]) => any>(func: T, delay: number): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// 🚀 PERFORMANCE: Optimized permission change handler with faster debounce

export default function RolesPermissions() {
  // 🔥 DEBUG: Add global function for testing permissions
  useEffect(() => {
    (window as any).testPermissions = async () => {
      try {
        const mapping = await roleService.testPermissionMapping();
        return mapping;
      } catch (error) {
        // Error handling
      }
    };

    return () => {
      delete (window as any).testPermissions;
    };
  }, []);


  // 🚀 PERFORMANCE: Track component performance
  const { logReport } = usePerformanceTracking('RolesPermissions');

  const { toast } = useToast();
  const {
    roles,
    deleteRole,
    unassignRoleFromUser,
    hasPermission,
    users,
    user,
    refreshRoles,
    refreshUsers,
    newRoleName,
    setNewRoleName,
    newRolePermissions,
    setNewRolePermissions,
    newRoleDescription,
    setNewRoleDescription,
    isAssignUserDialogOpen,
    setIsAssignUserDialogOpen,
    roleToAssignUser,
    setRoleToAssignUser,
    userSearchTerm,
    setUserSearchTerm,
    roleSearchTerm,
    setRoleSearchTerm,
    viewUsersRole,
    setViewUsersRole,
    isLoading,
    setIsLoading,
    selectedTemplate,
    setSelectedTemplate,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    roleToDelete,
    setRoleToDelete,
    editingRole,
    setEditingRole,
    isEditModalOpen,
    setIsEditModalOpen,
    editingRolePermissions,
    setEditingRolePermissions,
    editingRoleName,
    setEditingRoleName,
    editingRoleDescription,
    setEditingRoleDescription,
    userCountsByRole,
    allPermissions,
    fetchAllPermissions,
    fetchRolePermissions,
    createRoleWithPermissions,
    transformPermissionsToArray,
    salesClientsFlag,
    projectManagementFlag,
    financeManagementFlag,
    fetchPermissionFlags
  } = useRoleManagement();

  // 🔥 DEBUG: Add test function after newRolePermissions is available
  useEffect(() => {
    (window as any).testRoleCreation = () => {
      // Test permission validation logic
      const hasAnyPermission = Object.values(newRolePermissions).some((module: any) =>
        Object.values(module).some((submodule: any) => {
          // Handle nested structure (like hiring.settings.status)
          if (submodule && typeof submodule === 'object' && !Array.isArray(submodule)) {
            return Object.values(submodule).some((nestedSubmodule: any) => {
              if (nestedSubmodule && typeof nestedSubmodule === 'object' && !Array.isArray(nestedSubmodule)) {
                return Object.values(nestedSubmodule).some((permission: any) => permission === true);
              }
              return nestedSubmodule === true;
            });
          }
          // Handle regular structure (like hiring.candidate)
          return Object.values(submodule).some((permission: any) => permission === true);
        })
      );

      return hasAnyPermission;
    };

    // Add function to check stored permissions
    (window as any).checkStoredPermissions = () => {
      const stored = roleService.listStoredPermissions();
      return stored;
    };

    // Add function to get permissions for a specific role
    (window as any).getRolePermissions = (roleName: string) => {
      const permissions = roleService.getStoredPermissions(roleName);
      return permissions;
    };

    // Add function to check permission access
    (window as any).checkPermissionAccess = () => {
      return true;
    };

    return () => {
      delete (window as any).testRoleCreation;
      delete (window as any).checkStoredPermissions;
      delete (window as any).getRolePermissions;
      delete (window as any).checkPermissionAccess;
    };
  }, [newRolePermissions]);

  // 🔥 NEW: State to control active tab for redirection after role creation
  const [activeTab, setActiveTab] = useState<string>(() => {
    // Set default tab based on permissions
    if (hasPermission('settings.role.view')) return "roles";
    if (hasPermission('settings.role.create')) return "create";
    return "roles";
  });

  // 🚀 PERFORMANCE: Log performance report on component unmount
  useEffect(() => {
    return () => {
      logReport();
    };
  }, [logReport]);


  // 🔥 NEW: Initialize permissions and roles on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {


        // Force fetch fresh roles from backend
        await refreshRoles();

        // Fetch users from backend
        await refreshUsers();

        // Fetch all available permissions
        await fetchAllPermissions();

        // Fetch permission flags for filtering
        await fetchPermissionFlags();


      } catch (error: any) {
        console.error('❌ Initialization error:', error);
        // Don't show error toast for permissions initialization failure
        // as it's not critical for basic functionality
        // Permissions initialization failed, continuing with default permissions
      }
    };

    // Initialize data - permissions API is optional
    initializeData();
  }, [fetchAllPermissions, refreshRoles, refreshUsers, fetchPermissionFlags]);

  // Debug jobPosting permissions
  useEffect(() => {
    if (user?.permissions?.hiring?.jobPosting) {

    }
  }, [user]);

  // 🔥 IMPROVED: Enhanced debounced save function for permissions with faster response

  // 🔥 ENHANCED: Improved handleEditClick with better error handling and fallbacks
  const handleEditClick = async (role: Role) => {
    if (!hasPermission('settings.role.edit')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to edit roles.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get role ID with multiple fallbacks
      const roleId = role.id || role._id || role.name || '';

      if (!roleId) {
        toast({
          title: "❌ Error",
          description: "Role identifier not found. Cannot edit permissions.",
          variant: "destructive",
        });
        return;
      }

      // 🔥 PERFORMANCE: Show modal immediately with cached data, then update
      setIsEditModalOpen(true);
      setEditingRole(role);
      setEditingRoleName(role.name || '');
      setEditingRoleDescription(role.description || '');

      // Start with role.permissions if available, otherwise use default structure
      let initialPermissions = role.permissions as UserPermissions || JSON.parse(JSON.stringify(ALL_POSSIBLE_PERMISSIONS));

      // Validate and clean permissions structure
      if (!validatePermissionStructure(initialPermissions)) {
        initialPermissions = JSON.parse(JSON.stringify(ALL_POSSIBLE_PERMISSIONS));
      }

      const transformedPermissions = transformBackendPermissions(initialPermissions);
      setEditingRolePermissions(transformedPermissions);

      // 🔥 BACKGROUND: Try to fetch fresh permissions from API (non-blocking)
      try {
        const freshPermissions = await fetchRolePermissions(roleId);

        const transformedFreshPermissions = transformBackendPermissions(freshPermissions);
        setEditingRolePermissions(transformedFreshPermissions);

      } catch (apiError: any) {
        // Don't show error toast since we already have cached data
      }

    } catch (error: any) {
      setIsEditModalOpen(false);
      setEditingRole(null);
      setEditingRolePermissions(null);

      toast({
        title: "❌ Error",
        description: "Failed to open role editor. Please try again.",
        variant: "destructive",
      });
    }
  };

  // 🔥 NEW: Enhanced permission transformation function with strict boolean conversion
  const transformBackendPermissions = useCallback((backendPerms: any): UserPermissions => {
    // Start with the default structure - all permissions set to false (boolean)
    const transformed: UserPermissions = {
      employee_management: {
        employees: { view: false, create: false, edit: false, delete: false },
        department: { view: false, create: false, edit: false, delete: false },
        agreement: { view: false, edit: false },
        compensation: { view: false, edit: false }
      },
      sales_management: {
        client: { view: false, create: false, edit: false, delete: false },
        proposal: { view: false, create: false, edit: false, delete: false },
        contract: { view: false, create: false, edit: false, delete: false },
        revenue: { view: false }
      },
      finance_management: {
        invoice: { view: false, create: false, edit: false, delete: false },
        expense: { view: false, create: false, edit: false, delete: false },
        financialReport: { view: false }
      },
      hiring: {
        candidate: { view: false, create: false, edit: false, delete: false },
        jobPosting: { view: false, create: false, edit: false, delete: false },
        onboarding: { view: false, create: false, edit: false, delete: false },
        settings: {
          onboardingTask: { view: false, create: false, edit: false, delete: false }
        }
      },
      project_management: {
        project: { view: false, create: false, edit: false, delete: false },
        task: { view: false, create: false, edit: false, delete: false },
        projectStatus: { view: false, create: false, edit: false, delete: false }
      },
      settings: {
        roleAndPermission: { view: false, create: false, edit: false, delete: false },
        jobTitle: { view: false, create: false, edit: false, delete: false }
      } as any,
      system: {
        system: { admin: false }
      }
    };

    // 🔥 BOOLEAN CONVERSION: Helper function to ensure strict boolean values
    const toBooleanStrict = (value: any): boolean => {
      if (value === true || value === 'true' || value === 1 || value === '1') {
        return true;
      }
      if (value === false || value === 'false' || value === 0 || value === '0' || value === null || value === undefined) {
        return false;
      }
      // For any other value, convert to boolean
      return Boolean(value);
    };

    // Handle empty or null permissions
    if (!backendPerms) {
      return transformed;
    }

    // Handle array format from backend (e.g., ["employee_management.employees.view"])
    if (Array.isArray(backendPerms)) {
      if (backendPerms.length === 0) {
        return transformed;
      }

      try {
        backendPerms.forEach((permission: any, _index: number) => {
          let permissionString: string;

          // Handle different permission formats
          if (typeof permission === 'string') {
            permissionString = permission;
          } else if (typeof permission === 'object' && permission !== null) {
            // Handle permission objects with 'name' field
            if (permission.name && typeof permission.name === 'string') {
              permissionString = permission.name;
            } else {
              return;
            }
          } else {
            return;
          }

          // Ensure permission string is not empty
          if (!permissionString.trim()) {
            return;
          }

          // Parse permission strings like "employee_management.employees.view" or "employees.create"
          const parts = permissionString.split('.');

          if (parts.length === 2) {
            // Handle format like "employees.create" - need to map to our structure
            let [moduleOrSubmodule, action] = parts;

            // Map common modules to our structure
            let module: string;
            let submodule: string;

            if (moduleOrSubmodule === 'employees') {
              module = 'employee_management';
              submodule = 'employees';
            } else if (moduleOrSubmodule === 'department') {
              module = 'employee_management';
              submodule = 'department';
            } else if (moduleOrSubmodule === 'agreement') {
              module = 'employee_management';
              submodule = 'agreement';
            } else if (moduleOrSubmodule === 'compensation') {
              module = 'employee_management';
              submodule = 'compensation';
            } else if (moduleOrSubmodule === 'candidate') {
              module = 'hiring';
              submodule = 'candidate';
            } else if (moduleOrSubmodule === 'jobPosting') {
              module = 'hiring';
              submodule = 'jobPosting';
            } else if (moduleOrSubmodule === 'interview') {
              module = 'hiring';
              submodule = 'interview';
            } else if (moduleOrSubmodule === 'onboarding') {
              module = 'hiring';
              submodule = 'onboarding';
            } else if (moduleOrSubmodule === 'user' || moduleOrSubmodule === 'role' || moduleOrSubmodule === 'permission') {
              module = 'settings';
              submodule = 'roleAndPermission';
              if (action === 'manage') {
                action = 'edit';
              }
            } else if (moduleOrSubmodule === 'jobTitle') {
              module = 'settings';
              submodule = 'jobTitle';
            } else if (moduleOrSubmodule === 'client') {
              module = 'sales_management';
              submodule = 'client';
            } else if (moduleOrSubmodule === 'proposal') {
              module = 'sales_management';
              submodule = 'proposal';
            } else if (moduleOrSubmodule === 'contract') {
              module = 'sales_management';
              submodule = 'contract';
            } else if (moduleOrSubmodule === 'revenue') {
              module = 'sales_management';
              submodule = 'revenue';
            } else if (moduleOrSubmodule === 'project') {
              module = 'project_management';
              submodule = 'project';
            } else if (moduleOrSubmodule === 'task') {
              module = 'project_management';
              submodule = 'task';
            } else if (moduleOrSubmodule === 'invoice') {
              module = 'finance_management';
              submodule = 'invoice';
            } else if (moduleOrSubmodule === 'expense') {
              module = 'finance_management';
              submodule = 'expense';
            } else if (moduleOrSubmodule === 'financialReport') {
              module = 'finance_management';
              submodule = 'financialReport';
            } else if (moduleOrSubmodule === 'projectStatus') {
              module = 'project_management';
              submodule = 'projectStatus';
            } else if (moduleOrSubmodule === 'system') {
              module = 'system';
              submodule = 'system';
            } else if (moduleOrSubmodule === 'status') {
              module = 'hiring';
              submodule = 'settings';
              // Special handling for nested structure
              if (transformed[module as keyof UserPermissions]) {
                const modulePerms = transformed[module as keyof UserPermissions] as any;
                if (modulePerms.settings && modulePerms.settings.status) {
                  if (modulePerms.settings.status[action] !== undefined) {
                    modulePerms.settings.status[action] = true;
                  }
                }
              }
              return; // Skip the normal processing since we handled it above
            } else if (moduleOrSubmodule === 'onboardingTask') {
              module = 'hiring';
              submodule = 'settings';
              // Special handling for nested structure
              if (transformed[module as keyof UserPermissions]) {
                const modulePerms = transformed[module as keyof UserPermissions] as any;
                if (modulePerms.settings && modulePerms.settings.onboardingTask) {
                  if (modulePerms.settings.onboardingTask[action] !== undefined) {
                    modulePerms.settings.onboardingTask[action] = true;
                  }
                }
              }
              return; // Skip the normal processing since we handled it above
            } else {
              return;
            }

            if (transformed[module as keyof UserPermissions]) {
              const modulePerms = transformed[module as keyof UserPermissions] as any;
              if (modulePerms[submodule]) {
                if (modulePerms[submodule][action] !== undefined) {
                  modulePerms[submodule][action] = true;
                }
              }
            }
          } else if (parts.length === 3) {
            // Handle format like "employee_management.employees.view" or "hiring.status.view" or "settings.jobTitle.view"
            const [module, submodule, action] = parts;

            if (transformed[module as keyof UserPermissions]) {
              const modulePerms = transformed[module as keyof UserPermissions] as any;

              // 🔥 FIX: Handle special cases where backend sends flat format but UI expects nested
              if (module === 'hiring' && (submodule === 'status' || submodule === 'onboardingTask')) {
                // Map to nested structure: hiring.status.view -> hiring.settings.status.view
                if (modulePerms.settings && modulePerms.settings[submodule]) {
                  if (modulePerms.settings[submodule][action] !== undefined) {
                    modulePerms.settings[submodule][action] = true;
                  }
                }
              } else if (module === 'project_management' && (submodule === 'project' || submodule === 'task' || submodule === 'projectStatus')) {
                // Handle project_management permissions
                if (modulePerms[submodule]) {
                  if (modulePerms[submodule][action] !== undefined) {
                    modulePerms[submodule][action] = true;
                  }
                }
              } else if (modulePerms[submodule]) {
                // Handle regular structure
                if (modulePerms[submodule][action] !== undefined) {
                  modulePerms[submodule][action] = true;
                }
              }
            }
          } else if (parts.length === 4) {
            // Handle format like "hiring.settings.status.view" for nested structure
            const [module, submodule, nestedSubmodule, action] = parts;

            if (transformed[module as keyof UserPermissions]) {
              const modulePerms = transformed[module as keyof UserPermissions] as any;
              if (modulePerms[submodule] && modulePerms[submodule][nestedSubmodule]) {
                if (modulePerms[submodule][nestedSubmodule][action] !== undefined) {
                  modulePerms[submodule][nestedSubmodule][action] = true;
                }
              }
            }
          }
        });
      } catch (error) {
        return transformed; // Return default structure on error
      }

      return transformed;
    }

    // Handle object format from backend
    if (backendPerms && typeof backendPerms === 'object') {
      // Check if the backend permissions have the expected structure
      const hasExpectedStructure = backendPerms.employee_management ||
        backendPerms.sales_management ||
        backendPerms.finance_management ||
        backendPerms.hiring ||
        backendPerms.project_management ||
        backendPerms.settings ||
        backendPerms.system;

      if (hasExpectedStructure) {
        // Merge backend permissions with our default structure
        Object.keys(backendPerms).forEach(moduleKey => {
          if (transformed[moduleKey as keyof UserPermissions] && backendPerms[moduleKey]) {
            const modulePerms = backendPerms[moduleKey];

            // Process each submodule
            Object.keys(modulePerms).forEach(submoduleKey => {
              if ((transformed[moduleKey as keyof UserPermissions] as any)[submoduleKey] && modulePerms[submoduleKey]) {
                const submodulePerms = modulePerms[submoduleKey];

                // Process each action
                Object.keys(submodulePerms).forEach(actionKey => {
                  if ((transformed[moduleKey as keyof UserPermissions] as any)[submoduleKey][actionKey] !== undefined) {
                    const value = submodulePerms[actionKey];
                    // 🔥 BOOLEAN CONVERSION: Convert any value to strict boolean
                    (transformed[moduleKey as keyof UserPermissions] as any)[submoduleKey][actionKey] = toBooleanStrict(value);
                  }
                });
              }
            });
          }
        });
      }
    }

    return transformed;
  }, []);

  // 🔥 NEW: Function to handle permission changes with local state first

  // Handle role update
  const handleUpdateRole = useCallback(async () => {
    if (!editingRole || !editingRolePermissions) {
      return;
    }

    try {
      const roleId = editingRole.id || editingRole._id || '';

      const permissionsArray = transformPermissionsToArray(editingRolePermissions);

      // 🔥 FIX: Always save changes, not just when name/description changes
      const roleUpdateData = {
        name: editingRoleName.trim(),
        description: editingRoleDescription.trim(),
        permissions: permissionsArray,
        isActive: true
      };

      await roleService.updateRole(roleId, roleUpdateData);

      // 🔥 NEW: Refresh roles in frontend to show updated data immediately
      await refreshRoles();

      toast({
        title: "✅ Role Updated",
        description: `Role "${editingRoleName}" has been updated successfully.`,
        duration: 2000,
      });

      setIsEditModalOpen(false);
      setEditingRole(null);
      setEditingRolePermissions(null);
      setEditingRoleName('');
      setEditingRoleDescription('');

    } catch (error: any) {
      toast({
        title: "❌ Update Failed",
        description: "Failed to update role. Please try again.",
        variant: "destructive",
      });
    }
  }, [editingRole, editingRolePermissions, editingRoleName, editingRoleDescription, transformPermissionsToArray, user, toast, refreshRoles]);

  // Filter roles based on search term
  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.name.toLowerCase().includes(roleSearchTerm.toLowerCase())
    );
  }, [roles, roleSearchTerm]);


  // Handle permission change for new role
  const handleNewRolePermissionChange = useCallback((module: string, submodule: string, action: string, checked: boolean) => {
    if (!hasPermission('settings.role.create')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to create new roles.",
        variant: "destructive",
      });
      return;
    }

    // 🔥 BOOLEAN VALIDATION: Ensure checked is strictly boolean
    const isCheckedBoolean = checked === true;

    setNewRolePermissions(prev => {
      const newPermissions = { ...prev };
      if (!newPermissions[module as keyof UserPermissions]) {
        // @ts-ignore
        newPermissions[module as keyof UserPermissions] = {};
      }

      // Handle nested structure (like "settings.status")
      if (submodule.includes('.')) {
        const [parentSubmodule, nestedSubmodule] = submodule.split('.');

        // @ts-ignore
        if (!newPermissions[module as keyof UserPermissions][parentSubmodule]) {
          // @ts-ignore
          newPermissions[module as keyof UserPermissions][parentSubmodule] = {};
        }

        // @ts-ignore
        if (!newPermissions[module as keyof UserPermissions][parentSubmodule][nestedSubmodule]) {
          // @ts-ignore
          newPermissions[module as keyof UserPermissions][parentSubmodule][nestedSubmodule] = {};
        }

        // @ts-ignore - 🔥 BOOLEAN CONVERSION: Set strictly boolean value
        newPermissions[module as keyof UserPermissions][parentSubmodule][nestedSubmodule][action] = isCheckedBoolean;
      } else {
        // Handle regular structure
        // @ts-ignore
        if (!newPermissions[module as keyof UserPermissions][submodule]) {
          // @ts-ignore
          newPermissions[module as keyof UserPermissions][submodule] = {};
        }
        // @ts-ignore - 🔥 BOOLEAN CONVERSION: Set strictly boolean value
        newPermissions[module as keyof UserPermissions][submodule][action] = isCheckedBoolean;
      }

      return newPermissions;
    });
  }, [hasPermission, toast, setNewRolePermissions]);

  const handleSelectAllModulePermissions = useCallback((module: string, shouldSelect: boolean) => {
    // 🔥 BOOLEAN VALIDATION: Ensure shouldSelect is strictly boolean
    const shouldSelectBoolean = shouldSelect === true;

    setNewRolePermissions(prev => {
      const newPermissions = { ...prev };
      const modulePermissions = (newPermissions as any)[module];
      if (modulePermissions) {
        Object.keys(modulePermissions).forEach(submodule => {
          // Special handling for sales_management modules
          if (module === 'sales_management') {
            const salesModules = ['client', 'proposal', 'contract'];
            if (salesModules.includes(submodule)) {
              // For client, proposal, contract - ensure all 4 actions exist and set them
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              const salesActions = ['view', 'create', 'edit', 'delete'];
              salesActions.forEach(action => {
                modulePermissions[submodule][action] = shouldSelectBoolean;
              });
              return;
            } else if (submodule === 'revenue') {
              // For revenue - only set view action
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              modulePermissions[submodule]['view'] = shouldSelectBoolean;
              return;
            }
          }

          // Special handling for finance_management modules
          if (module === 'finance_management') {
            if (submodule === 'financialReport') {
              // For financialReport - only view permission
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              modulePermissions[submodule]['view'] = shouldSelectBoolean;
              return;
            } else if (submodule === 'invoice' || submodule === 'expense') {
              // For invoice, expense - ensure all 4 actions exist and set them
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              const financeActions = ['view', 'create', 'edit', 'delete'];
              financeActions.forEach(action => {
                modulePermissions[submodule][action] = shouldSelectBoolean;
              });
              return;
            }
          }

          // Handle nested structure (like hiring.settings with nested submodules)
          if (typeof modulePermissions[submodule] === 'object' && modulePermissions[submodule] !== null) {
            // Check if this is a nested structure (like settings.status, settings.onboardingTask)
            const hasNestedSubmodules = Object.values(modulePermissions[submodule]).some(
              value => typeof value === 'object' && value !== null
            );

            if (hasNestedSubmodules) {
              // Handle nested structure (like hiring.settings.status)
              Object.keys(modulePermissions[submodule]).forEach(nestedSubmodule => {
                if (typeof modulePermissions[submodule][nestedSubmodule] === 'object' &&
                  modulePermissions[submodule][nestedSubmodule] !== null) {
                  Object.keys(modulePermissions[submodule][nestedSubmodule]).forEach(action => {
                    // 🔥 BOOLEAN CONVERSION: Set strictly boolean value
                    modulePermissions[submodule][nestedSubmodule][action] = shouldSelectBoolean;
                  });
                }
              });
            } else {
              // Handle regular structure (like hiring.candidate)
              Object.keys(modulePermissions[submodule]).forEach(action => {
                // 🔥 BOOLEAN CONVERSION: Set strictly boolean value
                modulePermissions[submodule][action] = shouldSelectBoolean;
              });
            }
          } else {
            // Handle regular structure
            Object.keys(modulePermissions[submodule]).forEach(action => {
              // 🔥 BOOLEAN CONVERSION: Set strictly boolean value
              modulePermissions[submodule][action] = shouldSelectBoolean;
            });
          }
        });
      }
      return newPermissions;
    });
  }, [setNewRolePermissions]);

  const areAllModulePermissionsSelected = (moduleKey: string): boolean => {
    if (!newRolePermissions) return false;
    const modulePermissions = (newRolePermissions as any)[moduleKey];
    if (!modulePermissions) return false;

    // Check if module has any permissions at all
    const hasAnyPermissions = Object.keys(modulePermissions).length > 0;
    if (!hasAnyPermissions) return true; // No permissions to select, so it's "all selected"

    return Object.keys(modulePermissions).every(submodule => {
      const submodulePermissions = modulePermissions[submodule];

      // Special handling for sales_management modules
      if (moduleKey === 'sales_management') {
        const salesModules = ['client', 'proposal', 'contract'];
        if (salesModules.includes(submodule)) {
          // For client, proposal, contract - check all 4 actions
          const salesActions = ['view', 'create', 'edit', 'delete'];
          return salesActions.every(action => submodulePermissions[action] === true);
        } else if (submodule === 'revenue') {
          // For revenue - only check view action
          return submodulePermissions['view'] === true;
        }
      }

      // Handle nested structure (like hiring.settings with nested submodules)
      if (typeof submodulePermissions === 'object' && submodulePermissions !== null) {
        // Check if this is a nested structure (like settings.status, settings.onboardingTask)
        const hasNestedSubmodules = Object.values(submodulePermissions).some(
          value => typeof value === 'object' && value !== null
        );

        if (hasNestedSubmodules) {
          // Handle nested structure (like hiring.settings.status)
          return Object.keys(submodulePermissions).every(nestedSubmodule => {
            const nestedSubmodulePermissions = submodulePermissions[nestedSubmodule];
            if (typeof nestedSubmodulePermissions === 'object' && nestedSubmodulePermissions !== null) {
              return Object.values(nestedSubmodulePermissions).every(permission => permission === true);
            }
            return nestedSubmodulePermissions === true;
          });
        } else {
          // Handle regular structure (like hiring.candidate)
          return Object.values(submodulePermissions).every(permission => permission === true);
        }
      }

      // Handle regular structure
      return Object.values(submodulePermissions).every(permission => permission === true);
    });
  };

  const countTotalPermissionsInGroup = (groupKey: string, modulePermissions: any): number => {
    if (!modulePermissions) return 0;
    const group = permissionGroups[groupKey as keyof typeof permissionGroups];
    if (!group) return 0;

    return Object.keys(group.modules).reduce((count, submoduleKey) => {
      const submodule = modulePermissions?.[submoduleKey];
      if (!submodule) return count;

      // Handle nested structures
      if (submoduleKey === 'settings' && groupKey === 'hiring') {
        const s = submodule as any;
        let nestedCount = 0;
        if (s.status) nestedCount += Object.keys(s.status).length;
        if (s.onboardingTask) nestedCount += Object.keys(s.onboardingTask).length;
        return count + nestedCount;
      }

      // Special cases to match PermissionGroup.tsx filtering
      if (submoduleKey === 'agreement' || submoduleKey === 'compensation') {
        return count + 2; // only view and edit are rendered
      }
      if (submoduleKey === 'revenue' || submoduleKey === 'financialReport') {
        return count + 1; // only view is rendered
      }

      // Default: count all keys in the object
      return count + Object.keys(submodule).length;
    }, 0);
  };

  const countAssignedPermissions = (groupKey: string, modulePermissions: any): number => {
    if (!modulePermissions) return 0;
    const group = permissionGroups[groupKey as keyof typeof permissionGroups];
    if (!group) return 0;

    return Object.keys(group.modules).reduce((count, submoduleKey) => {
      const submodule = modulePermissions?.[submoduleKey];
      if (!submodule) return count;

      // Handle nested structures
      if (submoduleKey === 'settings' && groupKey === 'hiring') {
        const s = submodule as any;
        let nestedAssigned = 0;
        if (s.status) nestedAssigned += Object.values(s.status).filter(v => v === true).length;
        if (s.onboardingTask) nestedAssigned += Object.values(s.onboardingTask).filter(v => v === true).length;
        return count + nestedAssigned;
      }

      // Filter actions for special modules
      const actions = Object.entries(submodule).filter(([action]) => {
        if (submoduleKey === 'agreement' || submoduleKey === 'compensation') {
          return action === 'view' || action === 'edit';
        }
        if (submoduleKey === 'revenue' || submoduleKey === 'financialReport') {
          return action === 'view';
        }
        return true;
      });

      const assignedInSub = actions.filter(([, value]) => value === true).length;
      return count + assignedInSub;
    }, 0);
  };

  // Handle template selection
  const handleTemplateSelect = useCallback((template: RoleTemplate) => {
    setSelectedTemplate(template);
    setNewRolePermissions(template.permissions);
    setNewRoleName(template.name);
    setNewRoleDescription(template.description);
    toast({
      title: "Template Applied!",
      description: `The "${template.name}" template is ready to be customized.`,
    });
  }, [setNewRoleName, setNewRolePermissions, setSelectedTemplate, setNewRoleDescription, toast]);

  // Handle role creation
  const handleCreateRole = useCallback(async () => {
    if (!newRoleName.trim()) {
      toast({
        title: "Error",
        description: "Role name is required.",
        variant: "destructive",
      });
      return;
    }

    if (!newRoleDescription.trim()) {
      toast({
        title: "Error",
        description: "Role description is required.",
        variant: "destructive",
      });
      return;
    }

    // Check if at least one permission is selected with proper typing
    const hasAnyPermission = Object.values(newRolePermissions).some((module: any) =>
      Object.values(module).some((submodule: any) => {
        // Handle nested structure (like hiring.settings.status)
        if (submodule && typeof submodule === 'object' && !Array.isArray(submodule)) {
          return Object.values(submodule).some((nestedSubmodule: any) => {
            if (nestedSubmodule && typeof nestedSubmodule === 'object' && !Array.isArray(nestedSubmodule)) {
              return Object.values(nestedSubmodule).some((permission: any) => permission === true);
            }
            return nestedSubmodule === true;
          });
        }
        // Handle regular structure (like hiring.candidate)
        return Object.values(submodule).some((permission: any) => permission === true);
      })
    );

    if (!hasAnyPermission) {
      toast({
        title: "No Permissions Selected",
        description: "Please select at least one permission for the role.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Use the new createRoleWithPermissions function that syncs with backend
      await createRoleWithPermissions(
        newRoleName,
        newRolePermissions,
        newRoleDescription
      );

      // 🔥 NEW: Refresh roles in frontend to show newly created role immediately
      await refreshRoles();

      // Reset form
      setNewRoleName('');
      setNewRolePermissions(JSON.parse(JSON.stringify(ALL_POSSIBLE_PERMISSIONS)));
      setNewRoleDescription('');
      setSelectedTemplate(null);

      // Show success message
      toast({
        title: "Success",
        description: `Role "${newRoleName}" created successfully! Permissions have been saved and will be applied.`,
      });

      // 🔥 NEW: Redirect to roles tab after successful creation
      setActiveTab("roles");
    } catch (error: any) {
      let errorMessage = "Failed to create role. Please try again.";

      // Handle specific error cases
      if (error?.response?.status === 400) {
        if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = "Invalid role data. Please check the role name and permissions.";
        }
      } else if (error?.response?.status === 409) {
        errorMessage = "A role with this name already exists. Please choose a different name.";
      } else if (error?.response?.status === 403) {
        errorMessage = "You don't have permission to create roles.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  }, [newRoleName, newRolePermissions, newRoleDescription, createRoleWithPermissions, toast, setSelectedTemplate, setNewRoleName, setNewRolePermissions, setNewRoleDescription, user, refreshRoles]);

  // Handle role deletion
  const handleDeleteRole = useCallback(async (role: Role) => {
    if (!hasPermission('settings.role.delete')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to delete roles.",
        variant: "destructive",
      });
      return;
    }

    // const userCount = userCountsByRole[role.name] || 0;

    // Always open the delete confirmation dialog.
    // It will show the number of users assigned, even if it's zero.
    setRoleToDelete(role);
    setIsDeleteDialogOpen(true);
  }, [hasPermission, userCountsByRole]);

  const confirmDeleteRole = useCallback(async () => {
    if (!roleToDelete) return;

    try {
      await deleteRole(roleToDelete.name);

      // 🔥 NEW: Refresh roles in frontend to remove deleted role immediately
      await refreshRoles();

      setIsDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (error) {
      console.error('Failed to delete role:', error);
    }
  }, [roleToDelete, deleteRole, setIsDeleteDialogOpen, setRoleToDelete, user, refreshRoles]);

  const handleEditRolePermissionChange = useCallback((module: string, submodule: string, action: string, checked: boolean) => {
    if (!editingRole) return;

    // 🔥 BOOLEAN VALIDATION: Ensure checked is strictly boolean
    const isCheckedBoolean = checked === true;

    // Update local state immediately for responsive UI
    setEditingRolePermissions(prev => {
      if (!prev) return prev;

      const newPermissions = JSON.parse(JSON.stringify(prev)); // Deep copy

      // Ensure the module exists
      if (!newPermissions[module as keyof UserPermissions]) {
        (newPermissions as any)[module] = {};
      }

      // Handle nested structure (like "settings.status")
      if (submodule.includes('.')) {
        const [parentSubmodule, nestedSubmodule] = submodule.split('.');

        // Ensure the parent submodule exists
        if (!(newPermissions[module as keyof UserPermissions] as any)[parentSubmodule]) {
          (newPermissions[module as keyof UserPermissions] as any)[parentSubmodule] = {};
        }

        // Ensure the nested submodule exists
        if (!(newPermissions[module as keyof UserPermissions] as any)[parentSubmodule][nestedSubmodule]) {
          (newPermissions[module as keyof UserPermissions] as any)[parentSubmodule][nestedSubmodule] = {};
        }

        // 🔥 BOOLEAN CONVERSION: Set the permission - ensure it's strictly boolean
        (newPermissions[module as keyof UserPermissions] as any)[parentSubmodule][nestedSubmodule][action] = isCheckedBoolean;
      } else {
        // Handle regular structure
        // Ensure the submodule exists
        if (!(newPermissions[module as keyof UserPermissions] as any)[submodule]) {
          (newPermissions[module as keyof UserPermissions] as any)[submodule] = {};
        }

        // For sales modules (client, proposal, contract), ensure all four actions exist
        const salesModules = ['client', 'proposal', 'contract'];
        if (module === 'sales_management' && salesModules.includes(submodule)) {
          const salesActions = ['view', 'create', 'edit', 'delete'];
          salesActions.forEach(salesAction => {
            if (!(newPermissions[module as keyof UserPermissions] as any)[submodule][salesAction]) {
              (newPermissions[module as keyof UserPermissions] as any)[submodule][salesAction] = false;
            }
          });
        }

        // For revenue module, ensure only view action exists
        if (module === 'sales_management' && submodule === 'revenue') {
          if (!(newPermissions[module as keyof UserPermissions] as any)[submodule]['view']) {
            (newPermissions[module as keyof UserPermissions] as any)[submodule]['view'] = false;
          }
        }

        // For finance modules - handle differently based on submodule
        if (module === 'finance_management') {
          if (submodule === 'financialReport') {
            // For financialReport - ensure only view action exists
            if (!(newPermissions[module as keyof UserPermissions] as any)[submodule]['view']) {
              (newPermissions[module as keyof UserPermissions] as any)[submodule]['view'] = false;
            }
          } else if (submodule === 'invoice' || submodule === 'expense') {
            // For invoice, expense - ensure all four actions exist
            const financeActions = ['view', 'create', 'edit', 'delete'];
            financeActions.forEach(financeAction => {
              if (!(newPermissions[module as keyof UserPermissions] as any)[submodule][financeAction]) {
                (newPermissions[module as keyof UserPermissions] as any)[submodule][financeAction] = false;
              }
            });
          }
        }

        // 🔥 BOOLEAN CONVERSION: Set the permission - ensure it's strictly boolean
        (newPermissions[module as keyof UserPermissions] as any)[submodule][action] = isCheckedBoolean;
      }

      return newPermissions;
    });

    // 🔥 FIX: Don't call handlePermissionChange here to avoid double updates
    // The permissions will be saved when the user clicks "Save Changes"
    // const roleId = editingRole.id || editingRole._id || '';
    // handlePermissionChange(roleId, module, submodule, action, checked);
  }, [editingRole, setEditingRolePermissions]);

  const handleSelectAllEditingModulePermissions = useCallback((module: string, shouldSelect: boolean) => {
    if (!editingRole || !editingRolePermissions) return;

    // 🔥 BOOLEAN VALIDATION: Ensure shouldSelect is strictly boolean
    const shouldSelectBoolean = shouldSelect === true;

    // Update local state immediately for responsive UI
    setEditingRolePermissions(prev => {
      if (!prev) return null;
      const newPermissions = JSON.parse(JSON.stringify(prev)); // Deep copy
      const modulePermissions = (newPermissions as any)[module];

      if (modulePermissions) {
        Object.keys(modulePermissions).forEach(submodule => {
          // Special handling for sales_management modules
          if (module === 'sales_management') {
            const salesModules = ['client', 'proposal', 'contract'];
            if (salesModules.includes(submodule)) {
              // For client, proposal, contract - ensure all 4 actions exist and set them
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              const salesActions = ['view', 'create', 'edit', 'delete'];
              salesActions.forEach(action => {
                modulePermissions[submodule][action] = shouldSelectBoolean;
              });
              return;
            } else if (submodule === 'revenue') {
              // For revenue - only set view action
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              modulePermissions[submodule]['view'] = shouldSelectBoolean;
              return;
            }
          }

          // Special handling for finance_management modules
          if (module === 'finance_management') {
            if (submodule === 'financialReport') {
              // For financialReport - only view permission
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              modulePermissions[submodule]['view'] = shouldSelectBoolean;
              return;
            } else if (submodule === 'invoice' || submodule === 'expense') {
              // For invoice, expense - ensure all 4 actions exist and set them
              if (!modulePermissions[submodule]) {
                modulePermissions[submodule] = {};
              }
              const financeActions = ['view', 'create', 'edit', 'delete'];
              financeActions.forEach(action => {
                modulePermissions[submodule][action] = shouldSelectBoolean;
              });
              return;
            }
          }

          // Handle nested structure (like hiring.settings with nested submodules)
          if (typeof modulePermissions[submodule] === 'object' && modulePermissions[submodule] !== null) {
            // Check if this is a nested structure (like settings.status, settings.onboardingTask)
            const hasNestedSubmodules = Object.values(modulePermissions[submodule]).some(
              value => typeof value === 'object' && value !== null
            );

            if (hasNestedSubmodules) {
              // Handle nested structure (like hiring.settings.status)
              Object.keys(modulePermissions[submodule]).forEach(nestedSubmodule => {
                if (typeof modulePermissions[submodule][nestedSubmodule] === 'object' &&
                  modulePermissions[submodule][nestedSubmodule] !== null) {
                  Object.keys(modulePermissions[submodule][nestedSubmodule]).forEach(action => {
                    // 🔥 BOOLEAN CONVERSION: Set strictly boolean value
                    modulePermissions[submodule][nestedSubmodule][action] = shouldSelectBoolean;
                  });
                }
              });
            } else {
              // Handle regular structure (like hiring.candidate)
              Object.keys(modulePermissions[submodule]).forEach(action => {
                // 🔥 BOOLEAN CONVERSION: Set strictly boolean value
                modulePermissions[submodule][action] = shouldSelectBoolean;
              });
            }
          } else {
            // Handle regular structure
            Object.keys(modulePermissions[submodule]).forEach(action => {
              // 🔥 BOOLEAN CONVERSION: Set strictly boolean value
              modulePermissions[submodule][action] = shouldSelectBoolean;
            });
          }
        });
      }

      return newPermissions;
    });

    // 🔥 FIX: Don't save automatically - let user save manually via "Save Changes" button
    // This prevents conflicts and gives user control over when to save
  }, [editingRole, editingRolePermissions, setEditingRolePermissions]);

  const areAllEditingModulePermissionsSelected = (moduleKey: string): boolean => {
    if (!editingRolePermissions) return false;
    const modulePermissions = (editingRolePermissions as any)[moduleKey];
    if (!modulePermissions) return false;

    // Check if module has any permissions at all
    const hasAnyPermissions = Object.keys(modulePermissions).length > 0;
    if (!hasAnyPermissions) {
      return true; // No permissions to select, so it's "all selected"
    }

    return Object.keys(modulePermissions).every(submodule => {
      const submodulePermissions = modulePermissions[submodule];

      // Special handling for sales_management modules
      if (moduleKey === 'sales_management') {
        const salesModules = ['client', 'proposal', 'contract'];
        if (salesModules.includes(submodule)) {
          // For client, proposal, contract - check all 4 actions
          const salesActions = ['view', 'create', 'edit', 'delete'];
          return salesActions.every(action => submodulePermissions[action] === true);
        } else if (submodule === 'revenue') {
          // For revenue - only check view action
          return submodulePermissions['view'] === true;
        }
      }

      // Special handling for finance_management modules
      if (moduleKey === 'finance_management') {
        if (submodule === 'financialReport') {
          // For financialReport - only check view action
          return submodulePermissions['view'] === true;
        } else if (submodule === 'invoice' || submodule === 'expense') {
          // For invoice, expense - check all 4 actions
          const financeActions = ['view', 'create', 'edit', 'delete'];
          return financeActions.every(action => submodulePermissions[action] === true);
        }
      }

      // Handle nested structure (like hiring.settings with nested submodules)
      if (typeof submodulePermissions === 'object' && submodulePermissions !== null) {
        // Check if this is a nested structure (like settings.status, settings.onboardingTask)
        const hasNestedSubmodules = Object.values(submodulePermissions).some(
          value => typeof value === 'object' && value !== null
        );

        if (hasNestedSubmodules) {
          // Handle nested structure (like hiring.settings.status)
          return Object.keys(submodulePermissions).every(nestedSubmodule => {
            const nestedSubmodulePermissions = submodulePermissions[nestedSubmodule];
            if (typeof nestedSubmodulePermissions === 'object' && nestedSubmodulePermissions !== null) {
              return Object.values(nestedSubmodulePermissions).every(permission => permission === true);
            }
            return nestedSubmodulePermissions === true;
          });
        } else {
          // Handle regular structure (like hiring.candidate)
          return Object.values(submodulePermissions).every(permission => permission === true);
        }
      }

      // Handle regular structure
      return Object.values(submodulePermissions).every(permission => permission === true);
    });
  };

  const handleAssignUserClick = (role: Role) => {
    if (!hasPermission('employees.edit')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to assign users to roles.",
        variant: "destructive",
      });
      return;
    }
    setRoleToAssignUser(role);
    setIsAssignUserDialogOpen(true);
    setUserSearchTerm('');
  };

  const handleViewUsersClick = (role: Role) => {
    if (!hasPermission('employees.view')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to view users.",
        variant: "destructive",
      });
      return;
    }
    setViewUsersRole(role);
  };

  const handleUnassignUser = async (userId: string) => {
    try {
      setIsLoading(true);

      // Unassign role from user (function automatically assigns default Employee role)
      await apiUnassignRoleFromUser(userId);

      toast({
        title: "✅ User Role Updated",
        description: "User has been assigned the default Employee role.",
        duration: 3000,
      });

      // Refresh all user data sources to update counts
      await Promise.all([
        fetchApiUsers(userSearchTerm), // Refresh employee list for assign dialog
        refreshRoles(), // Refresh roles to update counts
        refreshUsers() // 🔥 FIX: Also refresh users in AuthContext for count calculation
      ]);

      // All user lists refreshed after role unassignment
    } catch (error) {
      toast({
        title: "❌ Role Update Failed",
        description: "Failed to update user role. Please try assigning a different role instead.",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassignAllUsers = () => {
    if (viewUsersRole) {
      // Defensive: Ensure users is an array
      const usersArray = Array.isArray(users) ? users : [];
      usersArray.forEach(u => {
        if (u && u.role === viewUsersRole.name) {
          unassignRoleFromUser(u.id);
        }
      });
      toast({
        title: "All Users Unassigned",
        description: `All users have been unassigned from the "${viewUsersRole.name}" role.`,
      });
      setViewUsersRole(null);
    }
  };

  // State for API-fetched employees for Assign User dialog
  const [apiUsers, setApiUsers] = useState<Employee[]>([]);
  const [apiUsersLoading, setApiUsersLoading] = useState(false);
  const [apiUsersError, setApiUsersError] = useState<string | null>(null);

  const fetchApiUsers = useCallback(async (searchTerm: string) => {
    setApiUsersLoading(true);
    setApiUsersError(null);
    try {
      const allUsers = await getEmployees();
      const filteredUsers = allUsers.filter((user: Employee) => {
        const name = user.user
          ? `${user.user.firstName || ''} ${user.user.lastName || ''}`.trim().toLowerCase()
          : (user.firstName || '').toLowerCase();
        const email = user.user?.email?.toLowerCase() || (user.email || '').toLowerCase();

        return name.includes(searchTerm.toLowerCase()) || email.includes(searchTerm.toLowerCase());
      });
      setApiUsers(filteredUsers);
    } catch (error: any) {
      setApiUsersError("Failed to fetch users.");
    } finally {
      setApiUsersLoading(false);
    }
  }, []);

  const debouncedUserSearch = useMemo(() => {
    return debounce((term: string) => {
      fetchApiUsers(term);
    }, 300);
  }, [fetchApiUsers]);

  useEffect(() => {
    if (isAssignUserDialogOpen) {
      fetchApiUsers(userSearchTerm);
    }
  }, [isAssignUserDialogOpen, fetchApiUsers, userSearchTerm]);

  const handleAssignRoleToUser = async (userId: string) => {
    if (roleToAssignUser) {
      try {
        setIsLoading(true);

        // Use the role ID or _id for assignment
        const roleIdToAssign = roleToAssignUser.id || roleToAssignUser._id || roleToAssignUser.name;
        await apiAssignRoleToUser(userId, roleIdToAssign);

        toast({
          title: "✅ User Assigned",
          description: `User has been successfully assigned the "${roleToAssignUser.name}" role.`,
          duration: 3000,
        });

        // Refresh all user data sources to update counts
        await Promise.all([
          fetchApiUsers(userSearchTerm), // Refresh employee list for assign dialog
          refreshRoles(), // Refresh roles to update counts
          refreshUsers() // 🔥 FIX: Also refresh users in AuthContext for count calculation
        ]);

        // All user lists refreshed after role assignment
      } catch (error) {
        toast({
          title: "❌ Assignment Failed",
          description: "Failed to assign role to user. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsLoading(false);
      }
    }
  };


  // Debounced search handler
  const debouncedUserSearchHandler = useCallback(debouncedUserSearch, [debouncedUserSearch]);

  const handleUnassignAllFromRole = async () => {
    if (!roleToAssignUser) return;

    const usersToUnassign = apiUsers.filter(emp => {
      let isAssigned = false;
      const userRoles = emp.user?.roles || [];
      const empRole = emp.role;

      if (Array.isArray(userRoles)) {
        isAssigned = userRoles.some(userRole => {
          if (typeof userRole === 'string') return userRole === roleToAssignUser.name || userRole === roleToAssignUser._id || userRole === roleToAssignUser.id;
          if (typeof userRole === 'object' && userRole) return (userRole as any).name === roleToAssignUser.name || (userRole as any)._id === roleToAssignUser._id || (userRole as any).id === roleToAssignUser.id;
          return false;
        });
      }

      if (!isAssigned && empRole) {
        if (typeof empRole === 'string') isAssigned = empRole === roleToAssignUser.name || empRole === roleToAssignUser._id || empRole === roleToAssignUser.id;
        else if (typeof empRole === 'object') isAssigned = empRole.name === roleToAssignUser.name || (empRole as any)._id === roleToAssignUser._id || (empRole as any).id === roleToAssignUser.id;
      }

      if (!isAssigned && (emp as any).roleId) isAssigned = (emp as any).roleId === roleToAssignUser._id || (emp as any).roleId === roleToAssignUser.id;

      if (!isAssigned && (emp as any).roles && Array.isArray((emp as any).roles)) {
        isAssigned = (emp as any).roles.some((r: any) => {
          if (typeof r === 'string') return r === roleToAssignUser.name || r === roleToAssignUser._id || r === roleToAssignUser.id;
          if (typeof r === 'object' && r) return r.name === roleToAssignUser.name || r._id === roleToAssignUser._id || r.id === roleToAssignUser.id;
          return false;
        });
      }

      return isAssigned;
    }).map(emp => emp.user?._id || (emp as any)._id || (emp as any).id);

    if (usersToUnassign.length === 0) {
      toast({ title: "No users to update.", variant: "default" });
      return;
    }

    setIsLoading(true);
    try {
      // Unassign roles from all users (function automatically assigns default Employee role)
      await Promise.all(usersToUnassign.map(userId => apiUnassignRoleFromUser(userId)));
      toast({
        title: "✅ All Users Updated",
        description: `All users have been assigned the default Employee role.`,
      });
      await Promise.all([
        fetchApiUsers(userSearchTerm),
        refreshRoles(),
        refreshUsers()
      ]);
    } catch (error) {
      toast({ title: "❌ Role Update Failed", description: "Could not update all users. Please try assigning different roles instead.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  const assignUserDialog = useMemo(() => (
    <AssignUserDialog
      isOpen={isAssignUserDialogOpen}
      onOpenChange={setIsAssignUserDialogOpen}
      role={roleToAssignUser}
      users={apiUsers}
      userSearchTerm={userSearchTerm}
      onUserSearchChange={debouncedUserSearchHandler}
      onAssignUser={handleAssignRoleToUser}
      onUnassignUser={handleUnassignUser}
      onUnassignAll={handleUnassignAllFromRole}
      isLoading={apiUsersLoading}
      error={apiUsersError}
      isProcessing={isLoading}
    />
  ), [isAssignUserDialogOpen, roleToAssignUser, apiUsers, userSearchTerm, debouncedUserSearchHandler, handleAssignRoleToUser, handleUnassignUser, handleUnassignAllFromRole, apiUsersLoading, apiUsersError, isLoading]);

  if (!user) {
    return <div>Loading user context...</div>;
  }


  // 🔥 DEBUG: Comprehensive permission debugging
  const canViewRoles = hasPermission('settings.role.view');
  const canCreateRoles = hasPermission('settings.role.create');


  // Show loading state if roles are still being fetched
  if (!Array.isArray(roles)) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Roles & Permissions</h3>
          <p className="text-gray-500">Please wait while we load your data...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-4 p-4 pt-1 pb-10">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Roles & Permissions</h1>

          </div>
        </div>
        <RoleStats rolesCount={Array.isArray(roles) ? roles.length : 0} usersCount={Array.isArray(users) ? users.length : 0} permissionGroupsCount={allPermissions ? Object.keys(allPermissions).length : Object.keys(permissionGroups).length} />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            {canViewRoles && (
              <TabsTrigger value="roles" className="flex items-center gap-2"><Users className="h-4 w-4" />Roles</TabsTrigger>
            )}
            {canCreateRoles && (
              <TabsTrigger value="create" className="flex items-center gap-2"><Plus className="h-4 w-4" />Create Role</TabsTrigger>
            )}
          </TabsList>
          {canViewRoles && (
            <TabsContent value="roles" className="mt-6">
              <RolesTable
                roles={filteredRoles}
                userCountsByRole={userCountsByRole}
                roleSearchTerm={roleSearchTerm}
                onRoleSearchChange={setRoleSearchTerm}
                onEdit={handleEditClick}
                onDelete={handleDeleteRole}
                onAssignUser={handleAssignUserClick}
                onViewUsers={handleViewUsersClick}
                onCreateFirstRole={() => setRoleSearchTerm("")}
              />
            </TabsContent>
          )}
          {canCreateRoles && (
            <TabsContent value="create" className="mt-6">
              <CreateRoleForm
                roleName={newRoleName}
                roleDescription={newRoleDescription}
                permissions={newRolePermissions}
                templates={ROLE_TEMPLATES}
                selectedTemplate={selectedTemplate}
                permissionGroups={permissionGroups}
                onRoleNameChange={setNewRoleName}
                onRoleDescriptionChange={setNewRoleDescription}
                onPermissionChange={handleNewRolePermissionChange}
                onSelectAllPermissions={handleSelectAllModulePermissions}
                onTemplateSelect={handleTemplateSelect}
                onCreateRole={handleCreateRole}
                areAllModulePermissionsSelected={areAllModulePermissionsSelected}
                countAssignedPermissions={countAssignedPermissions}
                countTotalPermissionsInGroup={countTotalPermissionsInGroup}
                sales_clients={salesClientsFlag}
                project_management={projectManagementFlag}
                finance_management={financeManagementFlag}
              />
            </TabsContent>
          )}

        </Tabs>
        <DeleteRoleDialog
          isOpen={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          role={roleToDelete}
          onConfirm={confirmDeleteRole}
          userCount={roleToDelete ? userCountsByRole[roleToDelete.name] || 0 : 0}
        />
        <EditRoleDialog
          isOpen={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          role={editingRole}
          roleName={editingRoleName}
          roleDescription={editingRoleDescription}
          permissions={editingRolePermissions}
          permissionGroups={permissionGroups}
          onRoleNameChange={setEditingRoleName}
          onRoleDescriptionChange={setEditingRoleDescription}
          onPermissionChange={handleEditRolePermissionChange}
          onSelectAllPermissions={handleSelectAllEditingModulePermissions}
          areAllModulePermissionsSelected={areAllEditingModulePermissionsSelected}
          countAssignedPermissions={countAssignedPermissions}
          countTotalPermissionsInGroup={countTotalPermissionsInGroup}
          onSave={handleUpdateRole}
          sales_clients={salesClientsFlag}
          project_management={projectManagementFlag}
          finance_management={financeManagementFlag}
          onCancel={() => {
            setIsEditModalOpen(false);
            setEditingRole(null);
            setEditingRolePermissions(null);
            setEditingRoleName('');
            setEditingRoleDescription('');
          }}
        />
        {assignUserDialog}
        <ViewUsersDialog
          isOpen={!!viewUsersRole}
          onOpenChange={() => setViewUsersRole(null)}
          role={viewUsersRole}
          users={users.map(u => ({
            id: u.id,
            name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown User',
            email: u.email,
            role: u.role
          }))}
          onUnassignUser={handleUnassignUser}
          onUnassignAllUsers={handleUnassignAllUsers}
          isProcessing={apiUsersLoading}
        />
      </div>
    </div>
  );
} 